import {
  QuestionAttempt,
  QuestionnaireStep,
} from 'APIs/letrusAPI/questionnaire/types';
import {PaginatedRequest} from 'utils/types/store';
import {getOpenQuestionFeedbackCopy} from './openQuestionFeedbackCopy';

interface useQuestionProps {
  question?: QuestionnaireStep['question'];
  attempts?: PaginatedRequest<QuestionAttempt[]>;
}

export function useQuestion({question, attempts}: useQuestionProps) {
  const currentAnswer = attempts?.results[0];
  const choices = currentAnswer?.responses;
  const hasBeenAnswered = !!currentAnswer;

  const questionsWithoutFeedback = ['statement', 'rating', 'text'];

  const isQuestionWithFeedback = !questionsWithoutFeedback?.some(
    (type) => type === question?.question_type,
  );

  const isAnswerWithFeedback = currentAnswer?.responses?.some(
    (choice) => choice.feedback,
  );

  const isOpenQuestion = question?.question_type === 'text';

  // Open-text questions (dissertativas) don't use correct_feedback/incorrect_feedback at all —
  // those come back null even when there's real feedback. The AI-generated feedback for what the
  // student actually wrote lives in its own object instead (achado real, 2026-09-28, payload
  // passado pelo Gustavo: `correct_feedback`/`incorrect_feedback` null, `open_question_feedback.
  // feedback_text` com o texto de verdade).
  const openQuestionFeedback = currentAnswer?.open_question_feedback;

  // While the NLP model is still being trained its raw rationale reads too technical for the
  // student, so the dissertativa shows only the standard message keyed by the evaluator's score
  // (0 wrong, 1 partially right, 2 right). A question not corrected by AI has no
  // open_question_feedback and a score outside 0/1/2 yields no copy — either way the feedback box
  // is hidden rather than falling back to the technical text. Normalize null to undefined so the
  // hook's generalFeedback stays string | undefined (no null leaking to its consumers).
  const openQuestionFeedbackText =
    getOpenQuestionFeedbackCopy(openQuestionFeedback?.score) ?? undefined;

  const hasGeneralFeedback = isOpenQuestion
    ? !!openQuestionFeedbackText
    : !!currentAnswer?.correct_feedback || !!currentAnswer?.incorrect_feedback;

  const hasFeedback =
    !!hasGeneralFeedback ||
    question?.has_correct_answer ||
    isAnswerWithFeedback ||
    isQuestionWithFeedback;

  let generalFeedback: string | undefined;
  if (isOpenQuestion) {
    generalFeedback = openQuestionFeedbackText;
  } else if (currentAnswer?.is_correct) {
    generalFeedback = currentAnswer?.correct_feedback;
  } else {
    generalFeedback = currentAnswer?.incorrect_feedback;
  }

  return {
    hasFeedback,
    hasBeenAnswered,
    generalFeedback,
    hasGeneralFeedback,
    userChoices: choices,
    userAnswer: currentAnswer?.answer,
  };
}
